import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ResearchLayout, { useReveal } from "../ResearchLayout";
import { fetchPublicTeams, findTeam, memberImageUrl, ROLE_PALETTE } from "../researchApi";

/* The admin must create a Team named "Research" (or "Research Team") and attach members. */
const RESEARCH_TEAM_NAMES = ["Research", "Research Team", "Clinical Research", "Research Department"];

export default function Team() {
  useReveal();
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);

  useEffect(() => {
    let mounted = true;
    fetchPublicTeams().then((data) => {
      if (!mounted) return;
      setTeams(data);
      setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const members = useMemo(() => {
    const team = findTeam(teams, RESEARCH_TEAM_NAMES);
    return team?.teamMembers || [];
  }, [teams]);

  return (
    <ResearchLayout>
      <section
        style={{
          padding: "100px 0 60px",
          background: "linear-gradient(135deg, #0d3b6e 0%, #076cec 100%)",
          color: "#fff",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div className="rs-blob" style={{ width: 400, height: 400, background: "#04b3bf", top: "-150px", right: "-100px" }} />
        <div
          className="rs-blob"
          style={{ width: 350, height: 350, background: "#7c3aed", bottom: "-100px", left: "-100px", animationDelay: "2s" }}
        />
        <div className="rs-container rs-text-center" style={{ position: "relative", zIndex: 2 }}>
          <span className="rs-section-eyebrow" style={{ background: "rgba(255,255,255,0.15)", color: "#fff", backdropFilter: "blur(10px)" }}>
            Our People
          </span>
          <h1 style={{ fontSize: "clamp(2rem, 5vw, 3.4rem)", fontWeight: 800, margin: "12px 0 16px" }}>Meet the Team</h1>
          <p style={{ fontSize: "1.05rem", maxWidth: 760, margin: "0 auto", opacity: 0.85, lineHeight: 1.7 }}>
            A dedicated team of GCP-certified physicians, investigators, coordinators and support staff working with precision and compassion.
          </p>
        </div>
      </section>

      <section className="rs-section" style={{ paddingTop: 60 }}>
        <div className="rs-container">
          {loading ? (
            <LoadingState label="Loading team members…" />
          ) : members.length === 0 ? (
            <EmptyState
              title="No team members yet"
              body='Create a team named "Research" in the admin panel and add members to populate this page.'
            />
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: 24,
              }}
            >
              {members.map((m, i) => (
                <MemberCard key={m.id} m={m} index={i} />
              ))}
            </div>
          )}

          <div
            className="rs-reveal"
            style={{
              marginTop: 50,
              padding: 30,
              borderRadius: 18,
              background: "linear-gradient(135deg, #f0f7ff, #e6fffa)",
              border: "1px solid rgba(7, 108, 236,0.08)",
              textAlign: "center",
            }}
          >
            <h3 style={{ margin: "0 0 10px", fontSize: "1.3rem", fontWeight: 800, color: "var(--rs-dark)" }}>Want to collaborate?</h3>
            <p style={{ margin: "0 0 22px", color: "#6b7280" }}>
              Get in touch with our research team for sponsorships, collaborations, or career opportunities.
            </p>
            <Link to="/research/contact" className="rs-btn rs-btn-primary">
              <i className="fa-regular fa-envelope"></i>
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </ResearchLayout>
  );
}

/* ─── Reusable bits exported for IRB page ─────────────────────────────── */
export function MemberCard({ m, index }) {
  const [imgError, setImgError] = useState(false);
  const color = ROLE_PALETTE[index % ROLE_PALETTE.length];
  const showImage = m.hasImage && !imgError;

  return (
    <div
      className={`rs-reveal delay-${(index % 4) + 1}`}
      style={{
        background: "#fff",
        borderRadius: 18,
        overflow: "hidden",
        border: "1px solid rgba(7, 108, 236,0.08)",
        transition: "all 0.4s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-10px) scale(1.01)";
        e.currentTarget.style.boxShadow = "0 24px 50px rgba(7, 108, 236,0.18)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0) scale(1)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div style={{ background: color, height: 110, position: "relative", overflow: "hidden" }}>
        <div
          style={{
            position: "absolute",
            right: -30,
            top: -30,
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.15)",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: -20,
            bottom: -40,
            width: 100,
            height: 100,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.1)",
          }}
        />
      </div>
      <div style={{ padding: "0 26px 26px", textAlign: "center", position: "relative" }}>
        <div
          style={{
            width: 110,
            height: 110,
            borderRadius: "50%",
            background: "#fff",
            border: "5px solid #fff",
            boxShadow: "0 10px 24px rgba(7, 108, 236,0.18)",
            margin: "-55px auto 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--rs-primary)",
            fontSize: "2.4rem",
            backgroundImage: showImage ? "none" : "linear-gradient(135deg, #f0f7ff, #e6fffa)",
            overflow: "hidden",
          }}
        >
          {showImage ? (
            <img
              src={memberImageUrl(m.id)}
              alt={m.name}
              onError={() => setImgError(true)}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <i className="fa-regular fa-user-doctor"></i>
          )}
        </div>
        <h3 style={{ margin: "0 0 4px", fontSize: "1.1rem", fontWeight: 800, color: "var(--rs-dark)" }}>{m.name}</h3>
        <div style={{ fontSize: "0.82rem", color: "var(--rs-secondary)", fontWeight: 700, marginBottom: 12 }}>{m.designation}</div>
        {m.description && <p style={{ fontSize: "0.82rem", color: "#6b7280", lineHeight: 1.6, margin: 0 }}>{m.description}</p>}
      </div>
    </div>
  );
}

export function LoadingState({ label }) {
  return (
    <div style={{ padding: "60px 20px", textAlign: "center", color: "#6b7280" }}>
      <i className="fa-regular fa-spinner fa-spin" style={{ fontSize: "1.8rem", color: "var(--rs-primary)", marginBottom: 12 }}></i>
      <div>{label}</div>
    </div>
  );
}

export function EmptyState({ title, body }) {
  return (
    <div
      style={{
        padding: "50px 30px",
        textAlign: "center",
        background: "linear-gradient(135deg, #f0f7ff, #e6fffa)",
        borderRadius: 18,
        border: "1px dashed rgba(7, 108, 236,0.2)",
      }}
    >
      <div style={{ fontSize: "2rem", color: "var(--rs-primary)", marginBottom: 10 }}>
        <i className="fa-regular fa-folder-open"></i>
      </div>
      <h3 style={{ margin: "0 0 8px", color: "var(--rs-dark)", fontWeight: 800 }}>{title}</h3>
      <p style={{ color: "#6b7280", margin: 0 }}>{body}</p>
    </div>
  );
}
